import * as THREE from 'three';
import { Entity } from '../../entities';
import {
  createMatteMaterial,
  createGlowMaterial,
  fbm3d,
  palette
} from '../../visual';
import {
  getSharedHaloGeometry,
  getSharedNoduleGeometry
} from './OreGeometryCache';

export type AsteroidKind =
  | 'standard'
  | 'uranium'
  | 'gold'
  | 'iron'
  | 'quartz'
  | 'ice'
  | 'plasma';

/** Minimal interface an anchor (e.g. a Planet) needs to provide so the
 *  asteroid can keep its offset relative to it as the anchor moves. */
export interface AsteroidAnchor {
  getWorldPosition(out: THREE.Vector3): THREE.Vector3;
}

export interface AsteroidOptions {
  readonly position: THREE.Vector3;
  readonly size: number;
  readonly tint?: number;
  readonly detail?: 1 | 2 | 3;
  readonly elongation?: number;
  readonly kind?: AsteroidKind;
  /** Identifier of the pool this asteroid belongs to (a planetary ring/belt).
   *  Asteroids without a poolId belong to the global deep-drift respawn pool. */
  readonly poolId?: string;
  /** When set, the asteroid keeps its initial offset to this anchor and
   *  follows it every frame — needed for ring/belt rocks so they orbit
   *  the parent planet around the star instead of drifting away. */
  readonly anchor?: AsteroidAnchor;
}

const URANIUM_CORE_COLOR = 0xb8ff5a;
const URANIUM_RIM_COLOR = 0x5fd42a;
const URANIUM_HALO_COLOR = 0xd6ff7a;
const GOLD_METAL_COLOR = 0xffc954;
const GOLD_DARK_COLOR = 0xa56e1c;

// Stellar iron — dark metallic chunks with a steel-blue sheen.
const IRON_METAL_COLOR = 0x9fb6c8;
const IRON_DARK_COLOR = 0x3a4f63;
// Raw quartz — pale glassy crystals, slight blue tint.
const QUARTZ_CORE_COLOR = 0xeaf6ff;
const QUARTZ_RIM_COLOR = 0x7baad6;
const QUARTZ_HALO_COLOR = 0xcfe7ff;
// Ionised ice — cyan crystals with a luminous halo.
const ICE_CORE_COLOR = 0xb6f2ff;
const ICE_RIM_COLOR = 0x2b86a8;
const ICE_HALO_COLOR = 0x7ff3ff;
// Plasma crystal — magenta core with a pink halo, rare and showy.
const PLASMA_CORE_COLOR = 0xffb0e7;
const PLASMA_RIM_COLOR = 0x7a2061;
const PLASMA_HALO_COLOR = 0xff66c8;

interface Crater {
  readonly center: THREE.Vector3;
  readonly radius: number;
  readonly depth: number;
}

export class Asteroid extends Entity {
  readonly collisionRadius: number;
  readonly collisionStatic = true;
  readonly collisionRestitution = 0.25;
  readonly maxHealth: number;
  health: number;
  readonly baseTint: number;
  readonly kind: AsteroidKind;
  readonly uraniumYield: number;
  readonly goldYield: number;
  /** Yield for the four ore-bearing kinds added by the mining pass
   *  (iron, quartz, ice, plasma). Zero for legacy kinds. The single field
   *  keeps the loot router simple — it just maps kind → itemId and uses
   *  this number. */
  readonly oreYield: number;
  readonly size: number;
  readonly poolId: string | null;

  private readonly spinAxis: THREE.Vector3;
  private readonly spinSpeed: number;
  private readonly anchor: AsteroidAnchor | null;
  private readonly anchorOffset: THREE.Vector3 | null;
  private rockMaterial: THREE.MeshStandardMaterial | null = null;
  private hitFlash = 0;
  private baseEmissiveIntensity = 0;
  private orePulseMaterials: THREE.MeshBasicMaterial[] = [];
  private pulseTime = Math.random() * Math.PI * 2;
  private detailGroup: THREE.Group | null = null;
  private detailVisible = true;

  constructor(options: AsteroidOptions) {
    super();
    this.size = options.size;
    this.collisionRadius = options.size * 1.1;
    this.maxHealth = Math.max(2, Math.ceil(options.size * 0.85) + 1);
    this.health = this.maxHealth;
    this.kind = options.kind ?? 'standard';
    this.uraniumYield =
      this.kind === 'uranium' ? Math.max(1, Math.ceil(options.size * 0.6)) : 0;
    this.goldYield =
      this.kind === 'gold' ? Math.max(1, Math.ceil(options.size * 0.7)) : 0;
    // Slightly lower than gold/uranium per unit size — the new ores are
    // more abundant (higher spawn weights) so they shouldn't out-yield
    // the rare metals.
    this.oreYield =
      this.kind === 'iron' ||
      this.kind === 'quartz' ||
      this.kind === 'ice' ||
      this.kind === 'plasma'
        ? Math.max(1, Math.ceil(options.size * 0.5))
        : 0;
    this.poolId = options.poolId ?? null;
    this.object3D.name = 'Asteroid';
    this.object3D.position.copy(options.position);
    this.object3D.rotation.set(
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2
    );

    const tint = options.tint ?? palette.asteroid.body;
    this.baseTint = tint;
    this.object3D.add(
      this.buildRock(
        options.size,
        tint,
        options.detail ?? 2,
        options.elongation ?? 0.25
      )
    );

    if (this.kind !== 'standard') {
      // All the small, flair-only meshes live under a single group so we
      // can hide them in one shot when the rock is far from the camera.
      this.detailGroup = new THREE.Group();
      this.detailGroup.name = 'asteroid-detail';
      this.object3D.add(this.detailGroup);

      switch (this.kind) {
        case 'uranium':
          this.buildUraniumOre(options.size);
          break;
        case 'gold':
          this.buildGoldOre(options.size);
          break;
        case 'iron':
          this.buildIronOre(options.size);
          break;
        case 'quartz':
          this.buildCrystalOre(options.size, {
            core: QUARTZ_CORE_COLOR,
            rim: QUARTZ_RIM_COLOR,
            halo: QUARTZ_HALO_COLOR,
            haloOpacity: 0.32,
            coreEmissive: 0.55,
            rimEmissive: 0.25,
            spikeFlavour: 'shard'
          });
          break;
        case 'ice':
          this.buildCrystalOre(options.size, {
            core: ICE_CORE_COLOR,
            rim: ICE_RIM_COLOR,
            halo: ICE_HALO_COLOR,
            haloOpacity: 0.55,
            coreEmissive: 1.0,
            rimEmissive: 0.45,
            spikeFlavour: 'spike'
          });
          break;
        case 'plasma':
          this.buildCrystalOre(options.size, {
            core: PLASMA_CORE_COLOR,
            rim: PLASMA_RIM_COLOR,
            halo: PLASMA_HALO_COLOR,
            haloOpacity: 0.6,
            coreEmissive: 1.6,
            rimEmissive: 0.7,
            spikeFlavour: 'spike'
          });
          break;
      }
    }

    this.spinAxis = new THREE.Vector3(
      Math.random() - 0.5,
      Math.random() - 0.5,
      Math.random() - 0.5
    ).normalize();
    this.spinSpeed = 0.05 + Math.random() * 0.15;

    if (options.anchor) {
      this.anchor = options.anchor;
      const anchorPos = new THREE.Vector3();
      this.anchor.getWorldPosition(anchorPos);
      this.anchorOffset = options.position.clone().sub(anchorPos);
    } else {
      this.anchor = null;
      this.anchorOffset = null;
    }
  }

  override update(dt: number): void {
    if (this.anchor && this.anchorOffset) {
      this.anchor.getWorldPosition(_anchorTmp);
      this.object3D.position.copy(_anchorTmp).add(this.anchorOffset);
    }

    // LOD: when far from the camera the player can't see spin direction or
    // pulse phase, and the ore vein meshes are sub-pixel. Skip those updates
    // and hide the detail children — frustum culling already handles the
    // rock mesh, this just frees GPU work for stuff that *is* on screen.
    const distSq = _cameraPos
      ? this.object3D.position.distanceToSquared(_cameraPos)
      : 0;
    const animate = distSq < ASTEROID_ANIMATE_DIST_SQ;
    const showDetail = distSq < ASTEROID_DETAIL_DIST_SQ;

    if (animate) {
      // Coming out of sleep: re-enable matrix recomputation so the spin we're
      // about to apply actually shows up.
      if (!this.anchor && !this.object3D.matrixAutoUpdate) {
        this.object3D.matrixAutoUpdate = true;
      }

      this.object3D.rotateOnAxis(this.spinAxis, this.spinSpeed * dt);

      if (this.hitFlash > 0 && this.rockMaterial) {
        this.hitFlash = Math.max(0, this.hitFlash - dt);
        const flash = (this.hitFlash / 0.18) * 0.9;
        this.rockMaterial.emissiveIntensity = this.baseEmissiveIntensity + flash;
      }

      if (this.orePulseMaterials.length > 0) {
        this.pulseTime += dt * 2.4;
        const pulse = 0.65 + Math.sin(this.pulseTime) * 0.2;
        for (const mat of this.orePulseMaterials) {
          mat.opacity = pulse;
        }
      }
    } else if (!this.anchor && this.object3D.matrixAutoUpdate) {
      // Far, anchor-less rocks neither move nor spin — freeze the local matrix
      // so the renderer stops multiplying transforms that didn't change. Anchor
      // rocks orbit a planet so their position changes every frame; skip them.
      this.object3D.matrixAutoUpdate = false;
    }

    if (this.detailGroup && showDetail !== this.detailVisible) {
      this.detailGroup.visible = showDetail;
      this.detailVisible = showDetail;
    }
  }

  getCollisionCenter(out: THREE.Vector3): THREE.Vector3 {
    return out.copy(this.object3D.position);
  }

  takeDamage(amount: number): boolean {
    this.health -= amount;
    this.hitFlash = 0.18;
    return this.health <= 0;
  }

  private buildRock(
    size: number,
    tint: number,
    detail: 1 | 2 | 3,
    elongation: number
  ): THREE.Mesh {
    const geometry = new THREE.IcosahedronGeometry(1, detail);
    const position = geometry.attributes['position'] as THREE.BufferAttribute;

    const seed = new THREE.Vector3(
      Math.random() * 50,
      Math.random() * 50,
      Math.random() * 50
    );
    const axisScale = new THREE.Vector3(
      1 - elongation + Math.random() * elongation * 2,
      1 - elongation + Math.random() * elongation * 2,
      1 - elongation + Math.random() * elongation * 2
    );

    const craters = this.generateCraters(3 + Math.floor(Math.random() * 4));
    const ridgeDirection = randomUnitVector();
    const fissureDirection = randomUnitVector();

    const dir = new THREE.Vector3();
    for (let i = 0; i < position.count; i++) {
      dir.set(position.getX(i), position.getY(i), position.getZ(i)).normalize();

      const macro = fbm3d(
        dir.x * 1.3 + seed.x,
        dir.y * 1.3 + seed.y,
        dir.z * 1.3 + seed.z,
        4
      );
      const detailBumps = fbm3d(
        dir.x * 3.8 + seed.x,
        dir.y * 3.8 + seed.y,
        dir.z * 3.8 + seed.z,
        3
      );
      const microGrit = fbm3d(
        dir.x * 8.5 + seed.x,
        dir.y * 8.5 + seed.y,
        dir.z * 8.5 + seed.z,
        2
      );

      const ridge = Math.abs(dir.dot(ridgeDirection));
      const ridgeBoost = Math.pow(ridge, 8) * 0.12;

      const fissure = Math.abs(dir.dot(fissureDirection));
      const fissureCut = Math.pow(fissure, 22) * 0.08;

      let displacement =
        1.0 + macro * 0.22 + detailBumps * 0.08 + microGrit * 0.03 + ridgeBoost - fissureCut;

      for (const crater of craters) {
        const d = dir.distanceTo(crater.center);
        if (d < crater.radius) {
          const t = d / crater.radius;
          if (t < 0.78) {
            const bowl = 1 - t / 0.78;
            displacement -= crater.depth * bowl * bowl;
          } else {
            const rimT = (t - 0.78) / 0.22;
            displacement += crater.depth * 0.45 * Math.sin(rimT * Math.PI);
          }
        }
      }

      displacement = Math.max(displacement, 0.55);
      const worldR = displacement * size;
      position.setXYZ(
        i,
        dir.x * axisScale.x * worldR,
        dir.y * axisScale.y * worldR,
        dir.z * axisScale.z * worldR
      );
    }
    position.needsUpdate = true;
    geometry.computeVertexNormals();

    this.paintVertexColors(geometry, tint, seed, craters);

    const material = createMatteMaterial({
      color: 0xffffff,
      roughness: 0.97,
      flatShading: true,
      emissive: 0xff8a3a,
      emissiveIntensity: 0
    });
    material.vertexColors = true;
    material.needsUpdate = true;
    this.rockMaterial = material;

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'asteroid-rock';
    return mesh;
  }

  private generateCraters(count: number): Crater[] {
    const craters: Crater[] = [];
    for (let i = 0; i < count; i++) {
      craters.push({
        center: randomUnitVector(),
        radius: 0.22 + Math.random() * 0.24,
        depth: 0.15 + Math.random() * 0.12
      });
    }
    return craters;
  }

  private paintVertexColors(
    geometry: THREE.BufferGeometry,
    tint: number,
    seed: THREE.Vector3,
    craters: readonly Crater[]
  ): void {
    const position = geometry.attributes['position'] as THREE.BufferAttribute;
    const normal = geometry.attributes['normal'] as THREE.BufferAttribute;
    const colors = new Float32Array(position.count * 3);

    const base = new THREE.Color(tint);
    const shadow = base.clone().multiplyScalar(0.32);
    const highlight = base.clone().lerp(new THREE.Color(0xffffff), 0.28);
    const warm = new THREE.Color(palette.asteroid.warm).lerp(base, 0.35);
    const mineralCool = new THREE.Color(palette.asteroid.accent).lerp(base, 0.5);
    const tmp = new THREE.Color();
    const dir = new THREE.Vector3();
    const nrm = new THREE.Vector3();

    const mineralSeed = new THREE.Vector3(
      Math.random() * 40,
      Math.random() * 40,
      Math.random() * 40
    );

    for (let i = 0; i < position.count; i++) {
      dir.set(position.getX(i), position.getY(i), position.getZ(i)).normalize();
      nrm.set(normal.getX(i), normal.getY(i), normal.getZ(i));

      const n = fbm3d(
        dir.x * 2.2 + seed.x * 1.3,
        dir.y * 2.2 + seed.y * 1.3,
        dir.z * 2.2 + seed.z * 1.3,
        3
      );
      const facing = THREE.MathUtils.clamp(nrm.y * 0.5 + 0.5, 0, 1);
      const mixShadow = THREE.MathUtils.clamp(n * 0.5 + 0.5, 0, 1);

      let ao = 1;
      for (const crater of craters) {
        const d = dir.distanceTo(crater.center);
        if (d < crater.radius * 0.9) {
          const t = d / (crater.radius * 0.9);
          ao = Math.min(ao, 0.55 + t * 0.45);
        }
      }

      tmp.copy(shadow).lerp(highlight, mixShadow * 0.7 + facing * 0.3);
      tmp.multiplyScalar(ao);

      const warmth = fbm3d(
        dir.x * 1.6 + seed.x * 2.1,
        dir.y * 1.6 + seed.y * 2.1,
        dir.z * 1.6 + seed.z * 2.1,
        2
      );
      if (warmth > 0.15) {
        tmp.lerp(warm, Math.min((warmth - 0.15) * 1.2, 0.55));
      }

      const mineral = fbm3d(
        dir.x * 4.5 + mineralSeed.x,
        dir.y * 4.5 + mineralSeed.y,
        dir.z * 4.5 + mineralSeed.z,
        2
      );
      if (mineral > 0.32) {
        tmp.lerp(mineralCool, Math.min((mineral - 0.32) * 1.6, 0.45));
      }

      const speck = fbm3d(
        dir.x * 18 + seed.x * 3.7,
        dir.y * 18 + seed.y * 3.7,
        dir.z * 18 + seed.z * 3.7,
        1
      );
      if (speck > 0.55) {
        tmp.lerp(new THREE.Color(0xffffff), (speck - 0.55) * 0.35);
      }

      colors[i * 3] = tmp.r;
      colors[i * 3 + 1] = tmp.g;
      colors[i * 3 + 2] = tmp.b;
    }

    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  }

  private detailParent(): THREE.Object3D {
    return this.detailGroup ?? this.object3D;
  }

  /**
   * Uranium ore: glowing rocky nodules embedded in the asteroid surface,
   * showing only a portion of each lump above the rock — like raw ore
   * exposed by erosion. A pulsing additive halo over the exposed bit
   * keeps the "this thing is hot" read from a distance.
   */
  private buildUraniumOre(size: number): void {
    const depositCount = 3 + Math.floor(Math.random() * 3);

    // Glowing ore lump. depthTest stays ON (default) so the buried half
    // of each nodule is correctly hidden by the rock — disabling it would
    // bleed the lump through the silhouette and break the embedded look.
    const oreMat = createMatteMaterial({
      color: URANIUM_CORE_COLOR,
      roughness: 0.55,
      emissive: URANIUM_CORE_COLOR,
      emissiveIntensity: 1.4,
      flatShading: true
    });
    oreMat.toneMapped = false;

    const rimMat = createMatteMaterial({
      color: URANIUM_RIM_COLOR,
      roughness: 0.7,
      emissive: URANIUM_CORE_COLOR,
      emissiveIntensity: 0.6,
      flatShading: true
    });
    rimMat.toneMapped = false;

    // Additive halo so it ADDS green light over the rock instead of
    // alpha-blending it (which would tint the rock translucent and read
    // as transparency). Depth test culls the buried half automatically.
    const haloMat = createGlowMaterial(URANIUM_HALO_COLOR, 0.45, true);
    haloMat.transparent = true;
    haloMat.blending = THREE.AdditiveBlending;
    haloMat.depthWrite = false;
    this.orePulseMaterials.push(haloMat);

    for (let i = 0; i < depositCount; i++) {
      const group = new THREE.Group();
      group.renderOrder = 2;

      const noduleSize = size * (0.22 + Math.random() * 0.14);
      const nodule = buildOreNodule(noduleSize, oreMat);
      group.add(nodule);

      // Smaller secondary lump to break the silhouette and look like a
      // cluster rather than one round blob.
      if (Math.random() < 0.7) {
        const flake = buildOreNodule(noduleSize * 0.55, rimMat);
        flake.position.set(
          (Math.random() - 0.5) * noduleSize * 0.9,
          noduleSize * (0.35 + Math.random() * 0.25),
          (Math.random() - 0.5) * noduleSize * 0.9
        );
        group.add(flake);
      }

      // Halo is a unit-radius sphere shared across every uranium asteroid in
      // the sector — scaling the mesh gives the same look as a per-instance
      // SphereGeometry while saving hundreds of buffer allocations + VRAM.
      const halo = new THREE.Mesh(getSharedHaloGeometry(), haloMat);
      halo.scale.setScalar(noduleSize * 1.35);
      halo.renderOrder = 2;
      group.add(halo);

      const direction = randomUnitVector();
      const offset = size - noduleSize * (0.35 + Math.random() * 0.3);
      group.position.copy(direction).multiplyScalar(offset);
      group.rotation.set(
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2
      );
      this.detailParent().add(group);
    }
  }

  /**
   * Stellar iron: dense metallic chunks with a cool blue sheen. Visually
   * close to the gold builder but the metallic colours read as "industrial
   * material" rather than "valuable nugget", so players know it's the
   * common hauling-grade ore.
   */
  private buildIronOre(size: number): void {
    const depositCount = 4 + Math.floor(Math.random() * 3);

    const ironMat = new THREE.MeshStandardMaterial({
      color: IRON_METAL_COLOR,
      metalness: 0.92,
      roughness: 0.45,
      flatShading: true,
      emissive: 0x101820,
      emissiveIntensity: 0.18
    });

    const darkIronMat = new THREE.MeshStandardMaterial({
      color: IRON_DARK_COLOR,
      metalness: 0.85,
      roughness: 0.6,
      flatShading: true
    });

    for (let i = 0; i < depositCount; i++) {
      const group = new THREE.Group();
      group.renderOrder = 2;

      const mainSize = size * (0.16 + Math.random() * 0.10);
      const main = buildOreNodule(mainSize, ironMat);
      group.add(main);

      const satellites = 1 + Math.floor(Math.random() * 2);
      for (let j = 0; j < satellites; j++) {
        const r = mainSize * (0.45 + Math.random() * 0.25);
        const chunk = buildOreNodule(r, j === 0 ? ironMat : darkIronMat);
        chunk.position.set(
          (Math.random() - 0.5) * mainSize * 1.5,
          (Math.random() * 0.5 + 0.1) * mainSize,
          (Math.random() - 0.5) * mainSize * 1.5
        );
        group.add(chunk);
      }

      const direction = randomUnitVector();
      const offset = size - mainSize * (0.4 + Math.random() * 0.25);
      group.position.copy(direction).multiplyScalar(offset);
      group.rotation.set(
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2
      );
      this.detailParent().add(group);
    }
  }

  /**
   * Crystal-style ore (raw quartz, ionised ice, plasma crystal). Builds a
   * cluster of glowing shards or spikes with an additive halo so each kind
   * advertises its colour from a long distance away. The `spikeFlavour`
   * dial chooses between flat shards (quartz) and pointed spikes (ice /
   * plasma) so the three kinds read as visually distinct.
   */
  private buildCrystalOre(
    size: number,
    params: {
      readonly core: number;
      readonly rim: number;
      readonly halo: number;
      readonly haloOpacity: number;
      readonly coreEmissive: number;
      readonly rimEmissive: number;
      readonly spikeFlavour: 'shard' | 'spike';
    }
  ): void {
    const depositCount = 3 + Math.floor(Math.random() * 3);

    const coreMat = createMatteMaterial({
      color: params.core,
      roughness: 0.4,
      emissive: params.core,
      emissiveIntensity: params.coreEmissive,
      flatShading: true
    });
    coreMat.toneMapped = false;

    const rimMat = createMatteMaterial({
      color: params.rim,
      roughness: 0.6,
      emissive: params.core,
      emissiveIntensity: params.rimEmissive,
      flatShading: true
    });
    rimMat.toneMapped = false;

    const haloMat = createGlowMaterial(params.halo, params.haloOpacity, true);
    haloMat.transparent = true;
    haloMat.blending = THREE.AdditiveBlending;
    haloMat.depthWrite = false;
    this.orePulseMaterials.push(haloMat);

    for (let i = 0; i < depositCount; i++) {
      const group = new THREE.Group();
      group.renderOrder = 2;

      const noduleSize = size * (0.18 + Math.random() * 0.12);

      // Shape per flavour: quartz uses a stretched octahedron-ish shard,
      // ice/plasma use stacked cones for a sharper crystalline feel.
      if (params.spikeFlavour === 'shard') {
        const shard = new THREE.Mesh(
          new THREE.OctahedronGeometry(noduleSize, 0),
          coreMat
        );
        shard.scale.set(0.6, 1.4, 0.6);
        shard.rotation.set(
          Math.random() * Math.PI,
          Math.random() * Math.PI,
          Math.random() * Math.PI
        );
        group.add(shard);

        if (Math.random() < 0.7) {
          const flake = new THREE.Mesh(
            new THREE.OctahedronGeometry(noduleSize * 0.55, 0),
            rimMat
          );
          flake.scale.set(0.5, 1.1, 0.5);
          flake.position.set(
            (Math.random() - 0.5) * noduleSize * 0.8,
            noduleSize * (0.3 + Math.random() * 0.2),
            (Math.random() - 0.5) * noduleSize * 0.8
          );
          flake.rotation.set(
            Math.random() * Math.PI,
            Math.random() * Math.PI,
            Math.random() * Math.PI
          );
          group.add(flake);
        }
      } else {
        const tall = new THREE.Mesh(
          new THREE.ConeGeometry(noduleSize * 0.55, noduleSize * 1.6, 6),
          coreMat
        );
        tall.position.y = noduleSize * 0.55;
        group.add(tall);

        const flake = new THREE.Mesh(
          new THREE.ConeGeometry(noduleSize * 0.4, noduleSize * 1.1, 6),
          rimMat
        );
        flake.position.set(
          (Math.random() - 0.5) * noduleSize * 0.7,
          noduleSize * 0.35,
          (Math.random() - 0.5) * noduleSize * 0.7
        );
        flake.rotation.set(
          (Math.random() - 0.5) * 0.5,
          Math.random() * Math.PI * 2,
          (Math.random() - 0.5) * 0.5
        );
        group.add(flake);
      }

      const halo = new THREE.Mesh(getSharedHaloGeometry(), haloMat);
      halo.scale.setScalar(noduleSize * 1.5);
      halo.renderOrder = 2;
      group.add(halo);

      const direction = randomUnitVector();
      const offset = size - noduleSize * (0.3 + Math.random() * 0.25);
      group.position.copy(direction).multiplyScalar(offset);
      group.rotation.set(
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2
      );
      this.detailParent().add(group);
    }
  }

  /**
   * Gold ore: rough metallic chunks embedded in the rock, like raw nuggets
   * exposed in a vein. depthTest stays ON so the buried half of each chunk
   * is correctly hidden by the rock — disabling it makes nuggets bleed
   * through the silhouette and the rock looks transparent.
   */
  private buildGoldOre(size: number): void {
    const depositCount = 4 + Math.floor(Math.random() * 3);

    const goldMat = new THREE.MeshStandardMaterial({
      color: GOLD_METAL_COLOR,
      metalness: 1.0,
      roughness: 0.32,
      flatShading: true,
      emissive: 0x2a1c00,
      emissiveIntensity: 0.18
    });

    const darkGoldMat = new THREE.MeshStandardMaterial({
      color: GOLD_DARK_COLOR,
      metalness: 0.9,
      roughness: 0.55,
      flatShading: true
    });

    for (let i = 0; i < depositCount; i++) {
      const group = new THREE.Group();
      group.renderOrder = 2;

      // Main chunk — biggest visible nugget.
      const mainSize = size * (0.18 + Math.random() * 0.12);
      const main = buildOreNodule(mainSize, goldMat);
      group.add(main);

      // 1-2 smaller satellite chunks alongside, like ore broken across a
      // seam rather than a single round lump.
      const satellites = 1 + Math.floor(Math.random() * 2);
      for (let j = 0; j < satellites; j++) {
        const r = mainSize * (0.45 + Math.random() * 0.25);
        const chunk = buildOreNodule(r, j === 0 ? goldMat : darkGoldMat);
        chunk.position.set(
          (Math.random() - 0.5) * mainSize * 1.6,
          (Math.random() * 0.5 + 0.1) * mainSize,
          (Math.random() - 0.5) * mainSize * 1.6
        );
        group.add(chunk);
      }

      const direction = randomUnitVector();
      const offset = size - mainSize * (0.4 + Math.random() * 0.25);
      group.position.copy(direction).multiplyScalar(offset);
      group.rotation.set(
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2
      );
      this.detailParent().add(group);
    }
  }

}

const _anchorTmp = new THREE.Vector3();

// Camera reference shared by all asteroids for distance-based LOD. Set once
// per frame by AsteroidLodSystem; null before the first frame (then asteroids
// fall back to "always animate", which is the safe default).
let _cameraPos: THREE.Vector3 | null = null;
const ASTEROID_ANIMATE_DIST = 900;
// Ore deposits stay visible across most of the local play area so distant
// asteroids still advertise their type as nav cues — frustum culling on
// the parent Object3D keeps off-screen rocks out of the pipeline
// regardless of this distance.
const ASTEROID_DETAIL_DIST = 6000;
const ASTEROID_ANIMATE_DIST_SQ = ASTEROID_ANIMATE_DIST * ASTEROID_ANIMATE_DIST;
const ASTEROID_DETAIL_DIST_SQ = ASTEROID_DETAIL_DIST * ASTEROID_DETAIL_DIST;

export function setAsteroidCameraPosition(pos: THREE.Vector3 | null): void {
  _cameraPos = pos;
}

function randomUnitVector(): THREE.Vector3 {
  const theta = Math.random() * Math.PI * 2;
  const phi = Math.acos(2 * Math.random() - 1);
  return new THREE.Vector3(
    Math.sin(phi) * Math.cos(theta),
    Math.sin(phi) * Math.sin(theta),
    Math.cos(phi)
  );
}

/**
 * Builds a rocky ore lump. The chunky FBM-displaced shape now comes from a
 * pool of pre-baked unit-radius templates (see OreGeometryCache) — each call
 * picks a random template and scales it via the mesh transform, so a cluster
 * still reads as varied while the GPU sees a small handful of buffers.
 */
function buildOreNodule(
  radius: number,
  material: THREE.Material
): THREE.Mesh {
  const mesh = new THREE.Mesh(getSharedNoduleGeometry(), material);
  mesh.scale.setScalar(radius);
  mesh.renderOrder = 2;
  return mesh;
}
